import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, Send, Loader2, MessageCircle, CheckCheck, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Price — Chat (course live, trajets publiés, modération admin).
 * Polling léger toutes les 2,5 s : messages + « en train d'écrire » + lecture (Vu).
 *
 * Props : open, onClose, requestId, selfRole ("client"|"driver"|"admin"),
 * counterpartName, apiBaseOverride, subtitle, quickRepliesOverride.
 */
const DEFAULT_QUICK = [
  "J'arrive dans quelques minutes",
  "Je suis en route",
  "Je suis arrivé(e)",
  "Merci !",
  "OK",
];

// Suit la zone visible (au-dessus du clavier) pour caler le chat dessus.
export function useVisualViewport(open) {
  const [vp, setVp] = useState({ top: 0, height: typeof window !== "undefined" ? window.innerHeight : 800, kb: false });
  const maxH = useRef(0);
  useEffect(() => {
    if (!open) return undefined;
    const vv = window.visualViewport;
    const update = () => {
      const h = vv ? vv.height : window.innerHeight;
      maxH.current = Math.max(maxH.current, h, window.innerHeight);
      setVp({ top: vv ? vv.offsetTop : 0, height: h, kb: h < maxH.current * 0.8 });
    };
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [open]);
  return vp;
}

export function TypingBubble({ name }) {
  return (
    <div className="flex items-end gap-2" data-testid="chat-typing-indicator">
      <div className="rounded-2xl border-2 border-black bg-white px-3.5 py-2.5 shadow-[0_2px_0_0_#000]">
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-2 w-2 rounded-full bg-neutral-500 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s`, animationDuration: "0.9s" }}
            />
          ))}
        </div>
      </div>
      <span className="pb-1 font-[Outfit] text-[11px] font-bold text-neutral-500">{name} écrit…</span>
    </div>
  );
}

export default function ChatSheet({ open, onClose, requestId, selfRole, counterpartName, apiBaseOverride, subtitle, quickRepliesOverride }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState([]);
  const [otherReadAt, setOtherReadAt] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const lastAtRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const vp = useVisualViewport(open);

  const isAdminView = selfRole === "admin";
  const apiBase = apiBaseOverride || (isAdminView
    ? `/admin/price/ride-requests/${requestId}/messages`
    : `/price/ride-requests/${requestId}/messages`);
  const quickReplies = isAdminView ? [] : (quickRepliesOverride || DEFAULT_QUICK);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  const fetchMessages = useCallback(async (initial = false) => {
    if (!requestId) return;
    try {
      if (initial) setLoading(true);
      const params = lastAtRef.current && !initial ? `?since=${encodeURIComponent(lastAtRef.current)}` : "";
      const r = await api.get(`${apiBase}${params}`);
      setTyping(Array.isArray(r.data?.typing) ? r.data.typing : []);
      setOtherReadAt(r.data?.other_read_at || null);
      const items = Array.isArray(r.data?.items) ? r.data.items : [];
      if (items.length === 0) return;
      lastAtRef.current = items[items.length - 1].created_at;
      if (!initial && items.some((m) => m.sender_role && m.sender_role !== selfRole)) {
        try { navigator.vibrate?.(45); } catch { /* ignore */ }
      }
      setMessages((prev) => {
        if (initial) return items;
        const known = new Set(prev.map((m) => m.id));
        return [...prev, ...items.filter((m) => !known.has(m.id))];
      });
      setTimeout(() => scrollToBottom(!initial), 30);
      if (!isAdminView) api.post(`${apiBase}/read`).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 400 && open) onClose?.();
    } finally {
      if (initial) setLoading(false);
    }
  }, [requestId, open, onClose, scrollToBottom, apiBase, selfRole, isAdminView]);

  useEffect(() => {
    if (!open || !requestId) return undefined;
    lastAtRef.current = null;
    setMessages([]);
    setTyping([]);
    setOtherReadAt(null);
    fetchMessages(true);
    const t = setInterval(() => { if (!document.hidden) fetchMessages(false); }, 2500);
    const onVis = () => { if (!document.hidden) fetchMessages(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId]);

  useEffect(() => {
    const isTouch = window.matchMedia?.("(hover: none)")?.matches;
    if (open && !isTouch) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  // Bloque le scroll de la page derrière (évite que iOS fasse remonter le fond).
  useEffect(() => {
    if (!open) return undefined;
    const { overflow, position, width } = document.body.style;
    const y = window.scrollY;
    Object.assign(document.body.style, { overflow: "hidden", position: "fixed", width: "100%", top: `-${y}px` });
    return () => {
      Object.assign(document.body.style, { overflow, position, width, top: "" });
      window.scrollTo(0, y);
    };
  }, [open]);

  // Garde la liste collée en bas quand sa taille change (clavier, champ multi-lignes).
  const atBottomRef = useRef(true);
  useEffect(() => {
    const el = listRef.current;
    if (!open || !el || typeof ResizeObserver === "undefined") return undefined;
    const onScroll = () => { atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; };
    const ro = new ResizeObserver(() => { if (atBottomRef.current) el.scrollTop = el.scrollHeight; });
    el.addEventListener("scroll", onScroll, { passive: true });
    ro.observe(el);
    return () => { ro.disconnect(); el.removeEventListener("scroll", onScroll); };
  }, [open, loading]);
  useEffect(() => { if (open) setTimeout(() => scrollToBottom(false), 200); }, [vp.height, vp.kb, open, scrollToBottom]);
  useEffect(() => { if (typing.length) setTimeout(() => scrollToBottom(true), 30); }, [typing.length, scrollToBottom]);

  // Auto-hauteur du champ (1 à ~5 lignes).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text, open]);

  if (!open) return null;

  const pingTyping = () => {
    if (isAdminView) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2500) return;
    lastTypingSentRef.current = now;
    api.post(`${apiBase}/typing`).catch(() => {});
  };

  const sendText = async (raw) => {
    const v = (raw ?? text).trim();
    if (!v || sending) return;
    setSending(true);
    const optimistic = {
      id: `local-${Date.now()}`,
      sender_role: selfRole,
      sender_name: "Moi",
      text: v,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    if (raw == null) setText("");
    lastTypingSentRef.current = 0;
    setTimeout(() => scrollToBottom(true), 30);
    try {
      const r = await api.post(apiBase, { text: v });
      const real = r.data?.message;
      if (real) {
        setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? real : m)));
        lastAtRef.current = real.created_at;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message non envoyé");
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      if (raw == null) setText(v);
    } finally {
      setSending(false);
    }
  };

  // Dernier message envoyé par moi (pour « Envoyé / Vu »).
  const lastMineId = !isAdminView
    ? [...messages].reverse().find((m) => m.sender_role === selfRole)?.id
    : null;

  const sheetHeight = vp.kb ? vp.height - 8 : Math.min(vp.height * 0.78, vp.height - 32);

  return (
    <div
      className="fixed inset-x-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      style={{ top: vp.top, height: vp.height }}
      data-testid="chat-sheet"
      onClick={onClose}
    >
      <div
        className={
          "w-full sm:max-w-md bg-[#FFF8F1] border-2 border-black shadow-[0_8px_0_0_#000] flex flex-col overflow-hidden " +
          (vp.kb ? "rounded-3xl mx-1 mb-1" : "rounded-t-3xl sm:rounded-3xl")
        }
        onClick={(e) => e.stopPropagation()}
        style={{ height: sheetHeight, transition: "height 160ms ease-out" }}
      >
        <ChatHeader name={counterpartName} subtitle={subtitle} isAdminView={isAdminView} typing={typing} onClose={onClose} />

        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-2"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-testid="chat-messages"
        >
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#FF3B3B]" />
            </div>
          )}
          {!loading && messages.length === 0 && (
            <div className="text-center py-10 px-4">
              <p className="font-[Outfit] text-[13px] text-neutral-600">Aucun message pour l&apos;instant. Envoie un premier mot 👋</p>
            </div>
          )}
          {messages.map((m) => (
            <ChatBubble
              key={m.id}
              m={m}
              selfRole={selfRole}
              isAdminView={isAdminView}
              status={m.id === lastMineId ? (m._optimistic ? "sending" : (otherReadAt && otherReadAt >= m.created_at ? "seen" : "sent")) : null}
            />
          ))}
          {typing.length > 0 && <TypingBubble name={typing[0].name || "Quelqu'un"} />}
        </div>

        {quickReplies.length > 0 && !vp.kb && (
          <div
            className="flex-none border-t-2 border-black bg-[#FFF8F1] px-3 py-2 flex gap-2 overflow-x-auto scrollbar-none"
            data-testid="chat-quick-replies"
          >
            {quickReplies.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => sendText(q)}
                disabled={sending}
                data-testid={`chat-quick-${q.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
                className="flex-none whitespace-nowrap rounded-full border-2 border-black bg-white px-3 py-1.5 font-[Outfit] text-[12px] font-black text-black shadow-[0_2px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); sendText(); }}
          className="flex-none border-t-2 border-black bg-white px-3 py-2.5 flex items-end gap-2"
          style={{ paddingBottom: vp.kb ? 10 : "max(10px, env(safe-area-inset-bottom))" }}
        >
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            onChange={(e) => { setText(e.target.value); if (e.target.value.trim()) pingTyping(); }}
            placeholder="Écris un message…"
            maxLength={500}
            enterKeyHint="enter"
            data-testid="chat-input"
            className="flex-1 min-h-[44px] max-h-[120px] resize-none rounded-xl border-2 border-black px-3 py-2.5 font-[Outfit] font-medium leading-[1.35] bg-[#FFF8F1] focus:outline-none focus:bg-white"
            style={{ fontSize: "16px" }}
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            onMouseDown={(e) => e.preventDefault()}
            data-testid="chat-send"
            className="h-11 w-11 flex-none rounded-xl bg-[#FF3B3B] border-2 border-black grid place-items-center shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" strokeWidth={3} />}
          </button>
        </form>
      </div>
    </div>
  );
}

function ChatHeader({ name, subtitle, isAdminView, typing, onClose }) {
  return (
    <div className="flex-none flex items-center justify-between px-5 py-3 border-b-2 border-black bg-white" data-testid="chat-header">
      <div className="flex items-center gap-2 min-w-0">
        <div className={"w-9 h-9 rounded-xl border-2 border-black grid place-items-center shadow-[0_3px_0_0_#000] flex-none " + (isAdminView ? "bg-[#FFB800]" : "bg-[#FF3B3B]")}>
          <MessageCircle className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
        <div className="min-w-0">
          <div className="font-[Outfit] text-[15px] font-black leading-none truncate">{name || "Chat"}</div>
          <div className={"font-[Outfit] text-[11px] mt-1 truncate " + (typing.length ? "text-[#FF3B3B] font-bold" : "text-neutral-600")}>
            {typing.length
              ? `${typing[0].name || "Quelqu'un"} est en train d'écrire…`
              : (subtitle || (isAdminView ? "Modération admin en direct" : "Chat effacé à la fin de la course"))}
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="chat-close"
        onClick={onClose}
        className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center bg-white active:translate-y-[2px] transition-transform"
      >
        <X className="w-4 h-4" strokeWidth={3} />
      </button>
    </div>
  );
}

function ChatBubble({ m, selfRole, isAdminView, status }) {
  if (m.sender_role === "admin") {
    return (
      <div className="flex justify-center" data-testid="chat-msg-admin">
        <div className="max-w-[85%] rounded-2xl border-2 border-black bg-[#FFF3CC] px-3 py-2 shadow-[0_2px_0_0_#000]">
          <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest text-[#B58900] mb-1 flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B58900]" />
            {m.sender_name && m.sender_name !== "Support Price" ? `Support Price · ${m.sender_name}` : "Support Price"}
          </div>
          <div className="font-[Outfit] text-[13px] font-medium leading-snug whitespace-pre-wrap break-words text-black">{m.text}</div>
        </div>
      </div>
    );
  }
  const mine = m.sender_role === selfRole;
  return (
    <div className={"flex flex-col " + (mine ? "items-end" : "items-start")} data-testid={`chat-msg-${mine ? "mine" : "other"}`}>
      {isAdminView && (
        <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest mb-1 px-1">
          <span className={"px-1.5 py-0.5 rounded border border-black " + (m.sender_role === "driver" ? "bg-[#D6F5D6]" : "bg-[#FFE5E5]")}>
            {m.sender_role === "driver" ? "Covoitureur" : "Passager"} · {m.sender_name}
          </span>
        </div>
      )}
      <div className={"max-w-[78%] rounded-2xl border-2 border-black px-3 py-2 shadow-[0_2px_0_0_#000] " + (mine ? "bg-[#FF3B3B] text-white" : "bg-white text-black") + (m._optimistic ? " opacity-70" : "")}>
        <div className="font-[Outfit] text-[13px] font-medium leading-snug whitespace-pre-wrap break-words">{m.text}</div>
      </div>
      {status && (
        <div className="mt-0.5 px-1 flex items-center gap-1 font-[Outfit] text-[10px] font-bold text-neutral-500" data-testid={`chat-status-${status}`}>
          {status === "seen" && <><CheckCheck className="h-3 w-3 text-[#FF3B3B]" strokeWidth={3} />Vu</>}
          {status === "sent" && <><Check className="h-3 w-3" strokeWidth={3} />Envoyé</>}
          {status === "sending" && "Envoi…"}
        </div>
      )}
    </div>
  );
}
